import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cx } from 'class-variance-authority';
import React from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useInputError } from './hooks';

export interface OptionCard<T extends string = string> {
    value: T;
    icon: IconDefinition;
    label: React.ReactNode;
    description: React.ReactNode;
}

interface Props<T extends string> {
    name: string;
    options: OptionCard<T>[];
    ariaLabel?: string;
    /** clicking the selected card again clears the field (sets it to emptyValue) */
    allowDeselect?: boolean;
    emptyValue?: unknown;
    /** called with the picked value, for forms that save a choice right away */
    onChange?: (value: T | unknown) => void;
}

// Radio group rendered as selectable cards with icon, title and a short description.
export const FormOptionCards = <T extends string>({
    name,
    options,
    ariaLabel,
    allowDeselect = false,
    emptyValue = '',
    onChange: onValueChange
}: Props<T>) => {
    const { control } = useFormContext();
    const {
        field: { onChange, value }
    } = useController({ name, control });
    const message = useInputError(name, true);

    return (
        <div className="tw-mb-3">
            {/* auto-fit so the cards wrap instead of squeezing when the form sits in a narrow panel */}
            <div
                role="radiogroup"
                aria-label={ariaLabel}
                className="tw-grid tw-grid-cols-[repeat(auto-fit,minmax(200px,1fr))] tw-gap-4"
            >
                {options.map((option) => {
                    const isSelected = value === option.value;

                    return (
                        <button
                            key={option.value}
                            type="button"
                            role="radio"
                            aria-checked={isSelected}
                            onClick={() => {
                                const next = isSelected && allowDeselect ? emptyValue : option.value;
                                onChange(next);
                                onValueChange?.(next);
                            }}
                            className={cx(
                                'tw-card-bordered tw-card-clickable tw-flex tw-flex-col tw-gap-3 tw-p-5 tw-text-left tw-transition-colors',
                                'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-success',
                                isSelected ? 'tw-border-success tw-bg-successLight' : 'tw-bg-white hover:tw-border-accents-5'
                            )}
                        >
                            <span className="tw-flex tw-items-center tw-gap-3">
                                <span
                                    className={cx(
                                        'tw-flex tw-size-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-transition-colors',
                                        isSelected ? 'tw-bg-success tw-text-white' : 'tw-bg-accents-2 tw-text-accents-8'
                                    )}
                                >
                                    <FontAwesomeIcon icon={option.icon} />
                                </span>
                                <span className="tw-flex-1 tw-text-lg tw-font-semibold tw-text-accents-9">
                                    {option.label}
                                </span>
                                <span
                                    className={cx(
                                        'tw-flex tw-size-5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-2 tw-border-solid tw-text-[10px] tw-transition-colors',
                                        isSelected
                                            ? 'tw-border-success tw-bg-success tw-text-white'
                                            : 'tw-border-accents-5 tw-bg-white'
                                    )}
                                >
                                    {isSelected && <FontAwesomeIcon icon={faCheck} />}
                                </span>
                            </span>
                            <span className="tw-text-sm tw-leading-relaxed tw-text-accents-8">{option.description}</span>
                        </button>
                    );
                })}
            </div>
            {message}
        </div>
    );
};
